import React, { useState } from 'react';
import { creatorsAPI } from '../api/creators';
import { getFollowerLabel } from '../utils/platformHelpers';

const AddCreatorModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    platform: 'youtube',
    handle: '',
    profileUrl: '',
    followerCount: '',
    avgViews: '',
    avgEngagementRate: '',
    tier: 'micro',
    niche: '',
    contactEmail: '',
    country: '',
    language: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Convert niche string to array
      const nicheArray = formData.niche
        .split(',')
        .map(n => n.trim())
        .filter(n => n.length > 0);

      const dataToSend = {
        ...formData,
        followerCount: parseInt(formData.followerCount) || null,
        avgViews: parseInt(formData.avgViews) || null,
        avgEngagementRate: parseFloat(formData.avgEngagementRate) || null,
        niche: nicheArray
      };

      await creatorsAPI.create(dataToSend);
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        name: '',
        platform: 'youtube',
        handle: '',
        profileUrl: '',
        followerCount: '',
        avgViews: '',
        avgEngagementRate: '',
        tier: 'micro',
        niche: '',
        contactEmail: '',
        country: '',
        language: '',
        notes: ''
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add creator');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Add New Creator</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Ninja"
              />
            </div>

            {/* Platform */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Platform *
              </label>
              <select
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
              >
                <option value="youtube">YouTube</option>
                <option value="twitch">Twitch</option>
                <option value="kick">Kick</option>
                <option value="tiktok">TikTok</option>
              </select>
            </div>

            {/* Handle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Handle *
              </label>
              <input
                type="text"
                name="handle"
                value={formData.handle}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="ninja"
              />
            </div>

            {/* Profile URL */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile URL
              </label>
              <input
                type="url"
                name="profileUrl"
                value={formData.profileUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="https://twitch.tv/ninja"
              />
            </div>

            {/* Follower Count */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {getFollowerLabel(formData.platform)} Count
              </label>
              <input
                type="number"
                name="followerCount"
                value={formData.followerCount}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="18500000"
              />
            </div>

            {/* Avg Views */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Avg Views
              </label>
              <input
                type="number"
                name="avgViews"
                value={formData.avgViews}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="45000"
              />
            </div>

            {/* Engagement Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Engagement Rate (%)
              </label>
              <input
                type="number"
                step="0.1"
                name="avgEngagementRate"
                value={formData.avgEngagementRate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="8.4"
              />
            </div>

            {/* Tier */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier
              </label>
              <select
                name="tier"
                value={formData.tier}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
              >
                <option value="nano">Nano (1K-10K)</option>
                <option value="micro">Micro (10K-100K)</option>
                <option value="macro">Macro (100K-1M)</option>
                <option value="mega">Mega (1M+)</option>
              </select>
            </div>

            {/* Niche */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Niche/Games (comma-separated)
              </label>
              <input
                type="text"
                name="niche"
                value={formData.niche}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Fortnite, Valorant, Variety"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email
              </label>
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="creator@example.com"
              />
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="USA"
              />
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Any additional information..."
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-sc-pink text-white rounded-md hover:bg-sc-pink/90 disabled:bg-gray-400"
            >
              {loading ? 'Adding...' : 'Add Creator'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCreatorModal;
